import React, { FC, type MouseEventHandler, type ReactNode, useCallback, useEffect, useRef } from 'react';
import cn from '../../../../utils/classnames';
import withDropdownContext from '../../HOCs/with-dropdown-context';
import { DropdownOption } from '../../dropdown';
import useDropdownContext from '../../hooks/use-dropdown-context';
import { DropDirectionType, MenuAlignType } from '../../hooks/use-dropdown-position';
import './dropdown-button.scss';

export type DropdownButtonProps = {
    id?: string;
    title: ReactNode;
    children?: ReactNode;
    variant?: string;
    name?: string;
    disabled?: boolean;
    onClick?: MouseEventHandler;
    onSelect?: (
        arg: DropdownOption | undefined,
        eventKey?: string | null,
        event?: React.MouseEvent<Element, MouseEvent>
    ) => void;
    className?: string;
    autoClose?: boolean | 'inside' | 'outside' | 'select' | 'select-outside';
    // eslint-disable-next-line react/no-unused-prop-types
    dropDirection?: DropDirectionType;
    // eslint-disable-next-line react/no-unused-prop-types
    menuAlign?: MenuAlignType;
};

const DropdownButton: FC<DropdownButtonProps> = (props) => {
    const {
        id,
        title,
        name,
        children,
        variant = 'primary',
        disabled = false,
        className = '',
        autoClose = true,
        onClick,
        onSelect,
    } = props;

    const { isOpen, setIsOpen, menuRef, styles: positionStyles, isCalculating } = useDropdownContext();
    const containerRef = useRef<HTMLDivElement>(null);

    const toggleDropdown = (e: React.MouseEvent) => {
        if (disabled) {
            return;
        }
        setIsOpen((prev) => !prev);
        if (onClick) {
            onClick(e);
        }
    };

    const handleClose = useCallback(() => setIsOpen(false), []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!containerRef.current || !isOpen) {
                return;
            }

            const isClickInsideMenu = menuRef.current?.contains(event.target as Node);
            const isClickInsideToggle = containerRef.current.contains(event.target as Node);

            if (autoClose === true && !isClickInsideToggle) {
                handleClose();
            } else if (
                (autoClose === 'outside' || autoClose === 'select-outside') &&
                !isClickInsideToggle &&
                !isClickInsideMenu
            ) {
                handleClose();
            } else if (autoClose === 'inside' && isClickInsideMenu) {
                handleClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, autoClose, handleClose, menuRef]);

    const handleMenuClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const eventKey = target.getAttribute('data-event-key') || null;

        if (onSelect) {
            onSelect(undefined, eventKey, e);
        }

        if (autoClose === true || autoClose === 'inside') {
            handleClose();
        }
    };

    return (
        <div ref={containerRef} className={cn('dropdown-button dropdown', { [className]: true })} id={id}>
            <button
                type='button'
                className={cn('dropdown-toggle', { [variant]: true })}
                onClick={toggleDropdown}
                disabled={disabled}
                aria-expanded={isOpen}
                aria-haspopup='true'
                name={name}
            >
                <span className='dropdown-tittle'>{title}</span>
                <i className={cn('arrow-icon', { 'is-open': isOpen })} />
            </button>

            <ul
                ref={menuRef}
                className={cn('dropdown-menu', { visible: isOpen })}
                style={{
                    ...positionStyles,
                    visibility: isOpen && !isCalculating ? 'visible' : 'hidden'
                }}
                onClick={handleMenuClick}
                role='menu'
            >
                {children}
            </ul>
        </div>
    );
};

export default withDropdownContext(DropdownButton as FC<Partial<DropdownButtonProps>>);
